import React from "react";
import { Trans } from "react-i18next";
import DOMPurify from "dompurify";
import { formatCurrency, formatDate } from "../../utils/countryLocaleMap";
import { decodeHtmlEntities } from "../../utils/htmlHelpers";
import "../../i18n";
import { Alert } from "./ui/Alert";
import { InfoCircle } from "./icons";

// Define a type for bookingData
export interface BookingData {
  customerNotes?: string[];
  date?: string;
  depositTypePayment?: string | boolean;
  depositTotal?: number;
  venue: {
    title: string;
    currency: string;
    country_code: string | null;
    timezone?: string;
  };
  amount?: number;
}

interface InfoProps {
  bookingData?: BookingData;
  amount?: number;
}

const Info: React.FC<InfoProps> = ({ bookingData, amount }) => {
  const currency = bookingData?.venue?.currency ?? "GBP";
  const countryCode = bookingData?.venue?.country_code ?? "GB";

  const formattedDate = bookingData?.date ? formatDate(bookingData.date, countryCode, "long") : undefined;

  const amountWithCurrency = formatCurrency(amount ?? bookingData?.depositTotal ?? 0, currency, countryCode);

  const customerNotesSection = Array.isArray(bookingData?.customerNotes) && bookingData.customerNotes.length > 0 && (
    <div className="gcw:mt-4">
      {bookingData.customerNotes.map((note, index) => {
        // Create a stable key using content hash and fallback to index
        const noteKey = `note-${note.slice(0, 50).replace(/[^a-zA-Z0-9]/g, "")}-${index}`;
        return (
          <div
            key={noteKey}
            className="gcw:mt-1"
            /* biome-ignore lint/security/noDangerouslySetInnerHtml: Content is sanitised with DOMPurify */
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(decodeHtmlEntities(note)) }}
          />
        );
      })}
    </div>
  );

  if (bookingData?.depositTypePayment === true || bookingData?.depositTypePayment === "true") {
    return (
      <>
        <Alert variant="warning" className="gcw:flex gcw:justify-between gcw:items-center widget-panel-notice">
          <span className="alert-icon">
            <InfoCircle color="#9a3412" size={16} className="gcw:me-bs-4" />
          </span>
          <div>
            <Trans
              i18nKey="payment_requested"
              values={{
                // amount: bookingData.depositTotal,
                amount: amountWithCurrency,
                venue: bookingData?.venue?.title ?? "",
                date: formattedDate ?? "",
              }}
              components={{ strong: <strong /> }}
            />
          </div>
        </Alert>
        {customerNotesSection}
      </>
    );
  }
  return (
    <>
      <Alert variant="warning" className="gcw:flex gcw:justify-between gcw:items-center widget-panel-notice">
        <span className="alert-icon">
          <InfoCircle color="#9a3412" size={16} className="gcw:me-bs-4" />
        </span>
        <div>
          <Trans
            i18nKey="secure_booking"
            values={{
              amount: amountWithCurrency,
              venue: bookingData?.venue?.title ?? "",
            }}
            components={{ strong: <strong /> }}
          />
        </div>
      </Alert>
      {customerNotesSection}
    </>
  );
};

export default Info;
